import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Pill } from '../ui/Pill'
import type { ProviderSyncProgressSnapshot, ProviderSyncRunStatus } from './providerSyncProgress'

interface ProviderSyncProgressCardProps {
  run: ProviderSyncProgressSnapshot
  title: ReactNode
  finalSummary?: ReactNode
  details?: ReactNode
}

function runTone(status: ProviderSyncRunStatus): 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand' | 'coral' {
  if (status === 'SUCCEEDED') return 'success'
  if (status === 'SUCCEEDED_WITH_ISSUES') return 'warning'
  if (status === 'FAILED') return 'danger'
  if (status === 'RATE_LIMITED') return 'warning'
  return 'info'
}

function formatDateTime(value: string | null | undefined, locale: string) {
  if (!value) return null
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
}

export function ProviderSyncProgressCard({ run, title, finalSummary, details }: ProviderSyncProgressCardProps) {
  const { t, i18n } = useTranslation()
  const total = run.totalItems ?? null
  const percentage = total && total > 0 ? Math.min(100, Math.round((run.fetched / total) * 100)) : null
  const retryAt = formatDateTime(run.retryAfterAt, i18n.language)

  return (
    <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 12, background: 'var(--ink-50)', border: '1px solid var(--border-subtle)', color: 'var(--fg)', fontSize: 13, lineHeight: 1.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div style={{ fontWeight: 800, color: 'var(--brand-900)' }}>{title}</div>
        <Pill tone={runTone(run.status)} size="sm" dot>{t(`providerSyncProgress.status.${run.status}`)}</Pill>
      </div>
      <div style={{ marginTop: 8, color: 'var(--fg-muted)' }}>
        {total
          ? t('providerSyncProgress.progressDeterminate', { fetched: run.fetched, total, pages: run.pageCount })
          : t('providerSyncProgress.progressIndeterminate', { fetched: run.fetched, pages: run.pageCount })}
      </div>
      {percentage !== null && (
        <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: 'var(--ink-100)', overflow: 'hidden' }}>
          <div style={{ width: `${percentage}%`, height: '100%', borderRadius: 999, background: 'var(--grad-brand)', transition: 'width var(--dur-base) var(--ease-out)' }} />
        </div>
      )}
      {run.status === 'RATE_LIMITED' && (
        <div style={{ marginTop: 10, padding: '9px 10px', borderRadius: 10, background: 'var(--warning-100)', color: 'var(--fg)', border: '1px solid var(--warning-100)' }}>
          {retryAt ? t('providerSyncProgress.rateLimitedWithTime', { retryAt }) : t('providerSyncProgress.rateLimited')}
        </div>
      )}
      {run.status === 'FAILED' && (
        <div style={{ marginTop: 10, padding: '9px 10px', borderRadius: 10, background: 'var(--danger-100)', color: 'var(--danger)' }}>
          {t('providerSyncProgress.failed', { code: run.errorCode ?? t('providerSyncProgress.unknownError') })}
        </div>
      )}
      {(run.status === 'SUCCEEDED' || run.status === 'SUCCEEDED_WITH_ISSUES') && finalSummary && (
        <div style={{ marginTop: 8 }}>{finalSummary}</div>
      )}
      {details && (
        <div style={{ marginTop: 10 }}>{details}</div>
      )}
    </div>
  )
}
